import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = ({ fullPage = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        building_type: '',
      });
      setIsSubmitted(true);
      toast.success('Tak for din henvendelse');
      
      // Track successful form submission in GA4
      if (typeof window.gtag === 'function') {
        // Send generate_lead event with GA4 recommended parameters
        window.gtag('event', 'generate_lead', {
          currency: 'DKK',
          value: 1
        });
      }
    } catch (error) {
      toast.error('Der opstod en fejl');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section 
      data-testid="contact-section" 
      className={`${fullPage ? 'pt-32 pb-24 min-h-screen' : 'py-24 md:py-32'} bg-[#1E2A24]`}
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-white/40 text-sm mb-4">
              Kontakt
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl lg:text-5xl font-medium text-white leading-tight">
              Lad os se på potentialet i jeres bygning
            </h2>
            
            <div className="mt-12 space-y-6">
              <div>
                <p className="text-white/40 text-sm mb-1">Email</p>
                <a 
                  href="mailto:info@dilling-el.dk" 
                  className="text-white hover:text-white/70 transition-colors"
                  data-testid="contact-email"
                >
                  info@dilling-el.dk
                </a>
              </div>
              <div>
                <p className="text-white/40 text-sm mb-1">Telefon</p>
                <a 
                  href="tel:+4546461234" 
                  className="text-white hover:text-white/70 transition-colors"
                  data-testid="contact-phone"
                >
                  +45 46 46 12 34
                </a>
              </div>
              <div>
                <p className="text-white/40 text-sm mb-1">Adresse</p>
                <p className="text-white">
                  Gammel Darupvej 5D<br />
                  4000 Roskilde
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {isSubmitted ? (
              <div className="flex flex-col items-start">
                <div className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center mb-8">
                  <Check size={20} className="text-white" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-4">
                  Tak for din henvendelse
                </h3>
                <p className="text-white/60 mb-8">
                  Vi vender tilbage inden for 1-2 hverdage.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  Send ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <p className="text-white/70 text-base mb-6">
                  Udfyld formularen, så kontakter vi jer for at aftale en gratis og uforpligtende gennemgang af jeres bygning.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/40 text-sm block mb-2">
                      Navn *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-0 border-b border-white/20 rounded-none px-0 py-3 focus:border-white transition-colors bg-transparent text-white placeholder:text-white/30"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-sm block mb-2">
                      Email *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-0 border-b border-white/20 rounded-none px-0 py-3 focus:border-white transition-colors bg-transparent text-white placeholder:text-white/30"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/40 text-sm block mb-2">
                      Telefon
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="border-0 border-b border-white/20 rounded-none px-0 py-3 focus:border-white transition-colors bg-transparent text-white placeholder:text-white/30"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-sm block mb-2">
                      Virksomhed
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="border-0 border-b border-white/20 rounded-none px-0 py-3 focus:border-white transition-colors bg-transparent text-white placeholder:text-white/30"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/40 text-sm block mb-2">
                    Besked *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="border-0 border-b border-white/20 rounded-none px-0 py-3 focus:border-white transition-colors resize-none bg-transparent text-white placeholder:text-white/30"
                    data-testid="contact-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-3 bg-white text-[#1E2A24] px-8 py-4 text-sm hover:bg-[#F4F2EE] transition-colors group mt-4"
                  data-testid="contact-submit-btn"
                >
                  <span>{isSubmitting ? 'Sender...' : 'Book gratis gennemgang'}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
